import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-period-control.css?inline';

// ---------------------------------------------------------------------------
// Preset definitions
// ---------------------------------------------------------------------------

export interface PkPreset {
    label: string;
    key:   string;
    days:  number;
}

export const PRESETS: PkPreset[] = [
    { label: 'Last 24h',  key: '24h',  days: 1   },
    { label: 'Last 7d',   key: '7d',   days: 7   },
    { label: 'Last 30d',  key: '30d',  days: 30  },
    { label: 'Last 90d',  key: '90d',  days: 90  },
];

function presetByKey(key: string): PkPreset {
    return PRESETS.find(p => p.key === key) ?? PRESETS[1]!; // default: 7d
}

function presetRange(preset: PkPreset): { from: string; to: string } {
    const now = new Date();
    const from = new Date(now.getTime() - preset.days * 24 * 60 * 60 * 1000);
    return {
        from: from.toISOString(),
        to:   now.toISOString(),
    };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * `<pk-period-control>` — date-range preset picker.
 *
 * Attributes:
 *   `default` — preset key to select initially: `24h` | `7d` | `30d` | `90d` (default `7d`).
 *
 * Events:
 *   `pk-period-change` — bubbles, composed; detail `{ from: ISO, to: ISO, label: string }`.
 */
export class PkPeriodControl extends HTMLElement {
    static readonly tagName = 'pk-period-control';

    static get observedAttributes(): string[] {
        return ['default'];
    }

    private readonly _root: ShadowRoot;
    private _active: PkPreset;

    constructor() {
        super();
        this._root   = this.attachShadow({ mode: 'open' });
        this._active = presetByKey(this.getAttribute('default') ?? '7d');
    }

    connectedCallback(): void {
        const defaultKey = this.getAttribute('default') ?? '7d';
        this._active = presetByKey(defaultKey);
        this._doRender();
        // Emit the initial period on connect so consumers don't need to know
        // the default.
        this._emit();
    }

    attributeChangedCallback(_name: string, _old: string | null, next: string | null): void {
        if (next !== null) {
            this._active = presetByKey(next);
            this._doRender();
            this._emit();
        }
    }

    /** Returns the currently selected preset. */
    get activePreset(): PkPreset { return this._active; }

    private _select(preset: PkPreset): void {
        this._active = preset;
        this._doRender();
        this._emit();
    }

    private _emit(): void {
        const { from, to } = presetRange(this._active);
        this.dispatchEvent(new CustomEvent('pk-period-change', {
            bubbles: true,
            composed: true,
            detail: {
                from,
                to,
                label: this._active.label,
            },
        }));
    }

    private template(): TemplateResult {
        return html`
            <style>${styles}</style>
            <div class="preset-group"
                role="group"
                aria-label="Time period">
                ${PRESETS.map(p => html`
                    <button class="preset-btn"
                        type="button"
                        aria-pressed=${this._active.key === p.key ? 'true' : 'false'}
                        @click=${() => this._select(p)}>
                        ${p.label}
                    </button>
                `)}
            </div>
        `;
    }

    private _doRender(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkPeriodControl.tagName)) {
    customElements.define(PkPeriodControl.tagName, PkPeriodControl);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-period-control': PkPeriodControl;
    }
}
